export interface TeamMemberProfile {
  slug: string;
  name: string;
  role: string;
  category: "leadership" | "finance" | "sales" | "powertrain" | "robotics" | "manufacturing" | "advisors";
  categoryLabel: string;
  img: string;
  objectPosition?: string;
  location: string;
  expertise: string[];
  shortBio: string;
  fullBio: string[];
  linkedinUrl: string;
  highlights: string[];
  qualification?: string;
  email?: string;
  featured?: boolean;
}

export const TEAM_MEMBERS: TeamMemberProfile[] = [
  {
    slug: "kaustubh-dhonde",
    name: "Kaustubh Dhonde",
    role: "Chief Executive Officer",
    category: "leadership",
    categoryLabel: "Executive Leadership",
    email: "kaustubh@autonxt.in",
    img: "/images/team/team/kaustubh.png",
    objectPosition: "50% 15%",
    location: "Thane, Maharashtra, India",
    expertise: ["EV Architecture", "Autonomous Systems", "Strategic Vision", "Venture Growth"],
    shortBio: "Visionary founder driving AutoNxt's mission to pioneer high-torque electric and autonomous tractors across India.",
    fullBio: [
      "Kaustubh Dhonde is the Founder and Chief Executive Officer of AutoNxt Automation, spearheading India's first indigenous high-torque electric and autonomous agricultural tractor platform built for sustainable productivity.",
      "With a strong engineering foundation in robotics, embedded systems, and deep-tech mobility, Kaustubh established AutoNxt to eradicate diesel operating costs and deliver intelligent, zero-emission mechanization for farmers.",
      "Under his strategic stewardship, AutoNxt has developed proprietary EV architectures, secured institutional capital, and validated robust production lines, establishing the company as a transformative pioneer in clean mobility."
    ],
    linkedinUrl: "https://www.linkedin.com/in/kaustubh-dhonde/",
    highlights: [
      "Forbes 30 Under 30 & National CleanTech Innovation Pioneer",
      "Architect of India's First High-Torque Electric Tractor Technology",
      "Directs 150+ Multidisciplinary Engineers, Roboticists & Agronomists"
    ],
    featured: true
  },
  {
    slug: "pankaj-goyal",
    name: "Pankaj Goyal",
    role: "Co-Founder & COO",
    category: "leadership",
    categoryLabel: "Executive Leadership",
    email: "pankaj@autonxt.in",
    img: "/images/team/team/pankaj.png",
    objectPosition: "50% 18%",
    location: "Thane, Maharashtra, India",
    expertise: ["Operations Strategy", "Manufacturing Scaling", "Supply Chain Resilience", "Institutional Alliances"],
    shortBio: "Operational strategist overseeing manufacturing scaling, institutional partnerships, and nationwide deployment.",
    fullBio: [
      "Pankaj Goyal is the Co-Founder and Chief Operating Officer at AutoNxt Automation, orchestrating manufacturing scale-up, vendor ecosystem development, and day-to-day operations across corporate and production facilities.",
      "Bringing extensive operational leadership, Pankaj has established high-throughput assembly protocols, lean manufacturing cells, and resilient Tier-1 supply lines that achieve stringent automotive-grade quality benchmarks.",
      "He actively steers commercial fleet deliveries, institutional alliances, and pan-India dealership expansion, ensuring seamless operational execution as AutoNxt transitions clean mobility into mass agricultural adoption."
    ],
    linkedinUrl: "https://www.linkedin.com/in/pankaj-goyal-89196b1b/",
    highlights: [
      "Architect of AutoNxt's High-Throughput Assembly Protocols",
      "Formulated Scalable Pan-India Distribution & Dealership Networks",
      "Leads Strategic Sourcing & Tier-1 Automotive Supplier Ecosystems"
    ],
    featured: true
  },
  {
    slug: "gaurav-borkar",
    name: "Gaurav Borkar",
    role: "Chief Financial Officer",
    category: "finance",
    categoryLabel: "Finance & Strategy",
    qualification: "MBA in Finance | CFA Level I",
    email: "gaurav@autonxt.in",
    img: "/images/team/team/gaurav.png",
    objectPosition: "50% 15%",
    location: "Thane, Maharashtra, India",
    expertise: [
      "Financial Strategy & Planning",
      "Fundraising & Investor Relations",
      "Corporate Finance & Treasury Management",
      "Budgeting, MIS & Financial Reporting",
      "Corporate Governance & Compliance",
      "Internal Controls & Process Development"
    ],
    shortBio: "Strategic finance leader directing capital allocation, corporate governance, investor relations, and financial planning.",
    fullBio: [
      "Gaurav Borkar is the Chief Financial Officer at AutoNxt Automation, leading the company's financial strategy, capital allocation, corporate governance, investor relations, and long-term fiscal planning initiatives.",
      "Holding an MBA in Finance and having completed CFA Level I, Gaurav brings extensive experience in building scalable financial frameworks, optimizing treasury management, and establishing rigorous internal controls.",
      "He partners closely with executive leadership to evaluate strategic expansions, support fundraising milestones, and align commercial goals with disciplined governance to generate enduring enterprise value."
    ],
    linkedinUrl: "https://www.linkedin.com/company/autonxt-automation",
    highlights: [
      "Holds an MBA in Finance & Completed CFA Level I",
      "Architects Scalable Corporate Financial Systems & Governance Frameworks",
      "Leads Strategic Capital Allocation, Treasury & Investor Relations"
    ],
    featured: true
  },
  {
    slug: "amit-kumar-shivhare",
    name: "Amit Kumar Shivhare",
    role: "Head – Accounts & Finance",
    category: "finance",
    categoryLabel: "Finance & Accounts",
    qualification: "MBA (Finance) | 15+ Years in Accounts & Finance",
    email: "amit@autonxt.in",
    img: "/images/team/team/amit.png",
    objectPosition: "50% 15%",
    location: "Thane, Maharashtra, India",
    expertise: [
      "Financial Planning & Budgeting",
      "Fund & Cash-Flow Management",
      "Compliance & Taxation",
      "Audits & Management Reporting (MIS)",
      "Process Improvement & Internal Controls",
      "Working Capital Optimization"
    ],
    shortBio: "Senior Accounts & Finance Leader with 15+ years of progressive experience driving financial strategy, governance and business performance.",
    fullBio: [
      "Senior Accounts & Finance Leader with 15+ years of progressive experience driving financial strategy, governance and business performance across EV/automobile manufacturing, technology & publishing, infrastructure and real estate.",
      "Currently leading the complete finance function of a venture-funded EV startup, with responsibility for financial planning, budgeting, cash-flow and fund management, costing, taxation, compliance, MIS, audits and management reporting.",
      "Proven ability to establish robust financial controls, optimize working capital and costs, strengthen processes and deliver investor-ready financial insights. Strategic business partner to senior management, combining strong financial acumen, operational leadership and data-driven decision-making to support sustainable growth and profitability."
    ],
    linkedinUrl: "https://www.linkedin.com/company/autonxt-automation",
    highlights: [
      "15+ Years in Accounts & Finance Leadership",
      "Cross-Sector Expertise in EV/Automobile Manufacturing, Tech, Infrastructure & Real Estate",
      "Leads Complete Finance Function: Planning, Fund Management, Taxation & Audits"
    ],
    featured: false
  },
  {
    slug: "pratik",
    name: "Pratik",
    role: "Head of FP&A",
    category: "finance",
    categoryLabel: "Finance & Accounts",
    qualification: "Chartered Accountant (CA)",
    email: "pratik@autonxt.in",
    img: "/images/team/team/pratik.png",
    objectPosition: "50% 16%",
    location: "Thane, Maharashtra, India",
    expertise: [
      "Corporate Finance & FP&A",
      "Financial Modelling & Projections",
      "Fundraising & Due Diligence",
      "Virtual CFO Advisory",
      "MIS & Performance Management",
      "Cash Flow & Liquidity Planning"
    ],
    shortBio: "Chartered Accountant leading corporate finance, FP&A, fundraising modelling, and strategic financial governance.",
    fullBio: [
      "Chartered Accountant with deep expertise in Corporate Finance, FP&A, Virtual CFO advisory, financial modelling, fundraising, business strategy, and performance management across high-growth ventures.",
      "Partners with leadership to establish scalable financial frameworks, strengthen cash flow visibility, and drive strategic growth. Experienced in translating complex data into actionable business insights through forecasting, budgeting, and executive reporting.",
      "Successfully contributed to a USD 15 million capital raise by leading investor projections and due diligence. Directs monthly MIS reporting, liquidity optimization, and KPI tracking to support sustainable growth and profitability."
    ],
    linkedinUrl: "https://www.linkedin.com/company/autonxt-automation",
    highlights: [
      "Chartered Accountant (CA) with Deep Corporate Finance & FP&A Acumen",
      "Contributed to USD 15M Capital Raise via Financial Modelling & Due Diligence",
      "Drives Scalable Financial Frameworks, Cash Flow Visibility & Strategic MIS"
    ],
    featured: false
  },
  {
    slug: "hari-kishan",
    name: "Hari Kishan",
    role: "Plant Head",
    category: "manufacturing",
    categoryLabel: "Plant & Manufacturing",
    email: "hari@autonxt.in",
    img: "/images/team/team/harikishan.jpg",
    objectPosition: "50% 16%",
    location: "Thane, Maharashtra, India",
    expertise: ["Plant Operations", "Production Engineering", "Assembly Automation", "Quality Assurance"],
    shortBio: "Manufacturing veteran orchestrating plant operations, assembly automation, and zero-defect production quality.",
    fullBio: [
      "Hari Kishan leads plant operations and production facilities at AutoNxt Automation, directing end-to-end tractor assembly, chassis fabrication, robotic workstation integration, and factory floor throughput.",
      "With decades of industrial engineering and automotive manufacturing leadership, Hari has instituted state-of-the-art lean production cells, standardized assembly procedures, and automated end-of-line testing benches.",
      "He ensures that every electric tractor manufactured meets rigorous durability benchmarks and heavy-duty agricultural safety standards, maintaining world-class assembly reliability across every commercial batch."
    ],
    linkedinUrl: "https://www.linkedin.com/company/autonxt-automation",
    highlights: [
      "Industrialized AutoNxt Electric Tractor Assembly Facility",
      "Pioneered Automated End-of-Line Dynamic Dyno Testing",
      "Maintains Zero-Defect Six Sigma Quality Protocols"
    ]
  },
  {
    slug: "sudipto-deb",
    name: "Sudipto Deb",
    role: "Lead Power Train Engineer",
    category: "powertrain",
    categoryLabel: "Powertrain & Battery",
    email: "sudipto@autonxt.in",
    img: "/images/team/team/sudipto.jpg",
    objectPosition: "50% 14%",
    location: "Thane, Maharashtra, India",
    expertise: ["High-Torque EV Powertrains", "PMSM Motor Controllers", "Power Electronics", "Drivetrain Efficiency"],
    shortBio: "High-voltage powertrain specialist designing high-torque PMSM motor drives, silicon-carbide inverters, and EV systems.",
    fullBio: [
      "Sudipto Deb leads high-voltage powertrain architecture and motor control engineering at AutoNxt Automation, developing the core electric propulsion systems that drive our zero-emission agricultural tractor platforms.",
      "His engineering focus centers on high peak-torque delivery at low RPMs, dynamic field-oriented control (FOC) algorithms, and silicon-carbide (SiC) inverter efficiency under demanding heavy-duty farm implement loads.",
      "Sudipto spearheads regenerative braking integration and high-efficiency reduction gearboxes, achieving over 92% peak system energy efficiency under grueling multi-season agricultural operations in diverse soil conditions."
    ],
    linkedinUrl: "https://www.linkedin.com/company/autonxt-automation",
    highlights: [
      "Engineered High-Torque Low-RPM Powertrain for Heavy Implements",
      "Achieved 92%+ Peak System Energy Efficiency Under Heavy Field Loads",
      "Developed Proprietary Traction & Torque Vectoring Algorithms"
    ]
  },
  {
    slug: "vamsi-atluri",
    name: "Vamsi Atluri",
    role: "Lead Mechanical Design Engineer",
    category: "powertrain",
    categoryLabel: "Chassis & Mechanical Engineering",
    email: "vamsi@autonxt.in",
    img: "/images/team/team/vamsi.png",
    objectPosition: "45% 20%",
    location: "Thane, Maharashtra, India",
    expertise: ["Chassis Dynamics", "3D CAD & FEA Analysis", "ROPS/FOPS Safety", "Hydraulic Architecture"],
    shortBio: "Mechanical innovator engineering modular chassis dynamics, electro-hydraulic systems, and certified ROPS structures.",
    fullBio: [
      "Vamsi Atluri is the lead mechanical innovator behind AutoNxt's modular tractor chassis architecture, electro-hydraulic implement hitch systems, and heavy-duty structural safety platforms for modern agricultural needs.",
      "Utilizing advanced finite element analysis (FEA) and multi-body dynamics simulations, Vamsi designs ultra-durable, low-center-of-gravity chassis capable of withstanding severe torsional stresses across varied soil profiles.",
      "He champions international Roll-Over Protective Structure (ROPS) certifications, operator ergonomics, and tool-free serviceability, delivering heavy-duty structural reliability with supreme operator comfort."
    ],
    linkedinUrl: "https://www.linkedin.com/company/autonxt-automation",
    highlights: [
      "Designed Low-Center-of-Gravity High-Durability Chassis Platform",
      "Certified ROPS & FOPS Structural Operator Safety Cages",
      "Engineered Ultra-Responsive Electro-Hydraulic 3-Point Hitch"
    ]
  },
  {
    slug: "dr-dharmateja-adapa",
    name: "Dr. Dharmateja Adapa",
    role: "Lead Robotics Engineer",
    category: "robotics",
    categoryLabel: "Robotics & Software",
    email: "dharmateja@autonxt.in",
    img: "/images/team/team/dharmateja.jpg",
    objectPosition: "50% 16%",
    location: "Thane, Maharashtra, India",
    expertise: ["Autonomous Robotics", "LiDAR/Camera Fusion", "Path Planning Algorithms", "Smart Agriculture"],
    shortBio: "Robotics pioneer developing RTK-GNSS autonomy, multi-sensor perception, and intelligent path planning algorithms.",
    fullBio: [
      "Dr. Dharmateja Adapa spearheads robotics research, autonomous vehicle intelligence, and smart farming perception systems at AutoNxt Automation, pioneering hands-free agricultural operations across Indian farmlands.",
      "His research bridges advanced sensor fusion combining RTK-GNSS, solid-state LiDAR, and stereoscopic vision with deep reinforcement learning algorithms to achieve centimeter-accurate furrow tracking and dynamic obstacle evasion.",
      "Dr. Dharmateja's autonomous guidance software enables full hands-free plowing and spraying cycles, dramatically reducing operator fatigue, optimizing field coverage, and cutting operational energy costs for farming communities."
    ],
    linkedinUrl: "https://www.linkedin.com/company/autonxt-automation",
    highlights: [
      "Developed Centimeter-Precision RTK-GNSS Autonomous Steering",
      "Integrated Multi-Sensor Obstacle Perception & Fail-Safe Emergency Braking",
      "Authored Multiple Peer-Reviewed Robotics & Automation Papers"
    ]
  },
  {
    slug: "tejesh-madireddy",
    name: "Tejesh Madireddy",
    role: "Lead Embedded Engineer",
    category: "robotics",
    categoryLabel: "Robotics & Software",
    email: "tejesh@autonxt.in",
    img: "/images/team/team/tejash.png",
    objectPosition: "50% 25%",
    location: "Thane, Maharashtra, India",
    expertise: ["Vehicle Control Units (VCU)", "AUTOSAR Architecture", "CAN-FD Protocols", "Real-Time Embedded Systems"],
    shortBio: "Embedded architect designing Vehicle Control Units (VCU), real-time RTOS firmware, and CAN-FD communications.",
    fullBio: [
      "Tejesh Madireddy architects the digital central nervous system of AutoNxt electric tractors, heading the design and firmware implementation of proprietary high-performance Vehicle Control Units (VCU) and compute nodes.",
      "His embedded architecture oversees high-speed CAN-FD communication buses, drive-by-wire subsystems, isolation fault monitoring, and safety-critical real-time operating systems (RTOS) engineered for harsh farm environments.",
      "Tejesh ensures synchronized coordination between high-voltage battery management systems, motor inverters, and autonomous modules, while embedding secure Over-The-Air (OTA) telemetry for continuous fleet optimization."
    ],
    linkedinUrl: "https://www.linkedin.com/company/autonxt-automation",
    highlights: [
      "Engineered Proprietary High-Performance AutoNxt VCU Firmware",
      "Implemented Robust Dual-Channel Safety Fault Isolation",
      "Architected Secure Over-the-Air (OTA) Diagnostics Gateways"
    ]
  },
  {
    slug: "aswanth-mulupuri",
    name: "Aswanth Mulupuri",
    role: "Robotics & Automation Engineer",
    category: "robotics",
    categoryLabel: "Robotics & Software",
    email: "aswanth@autonxt.in",
    img: "/images/team/team/sai.png",
    objectPosition: "50% 16%",
    location: "Thane, Maharashtra, India",
    expertise: ["Autonomous Controls", "Actuator Automation", "Computer Vision", "Agricultural Automation"],
    shortBio: "Automation specialist engineering electro-mechanical actuators, precision control loops, and computer vision.",
    fullBio: [
      "Aswanth Mulupuri specializes in developing precision control loops, smart electro-mechanical actuators, and computer vision perception algorithms for automated tractor field operations at AutoNxt Automation.",
      "He integrates camera-guided crop row following, automated implement depth modulation, and intelligent headland turning algorithms that execute precise boundary maneuvers without requiring manual driver steering intervention.",
      "His on-field validation ensures seamless switching between autonomous modes and manual operator controls, empowering farmers of all skill levels to achieve consistent agricultural precision and enhanced productivity."
    ],
    linkedinUrl: "https://www.linkedin.com/company/autonxt-automation",
    highlights: [
      "Engineered Automated Headland Turn & Furrow Tracking Loops",
      "Integrated Real-Time Electro-Mechanical Steering Actuators",
      "Validated Multi-Terrain Autonomous Field Traversals"
    ]
  },
  {
    slug: "ajinkya-delvi",
    name: "Ajinkya Delvi",
    role: "Power Train Engineer",
    category: "powertrain",
    categoryLabel: "Powertrain & Battery",
    email: "ajinkya@autonxt.in",
    img: "/images/team/team/ajinkya.png",
    objectPosition: "50% 16%",
    location: "Thane, Maharashtra, India",
    expertise: ["Thermal Management", "Liquid Cooling Systems", "Powertrain Optimization", "High-Voltage Testing"],
    shortBio: "Thermal engineer optimizing liquid cooling systems, high-voltage battery enclosures, and inverter heat dynamics.",
    fullBio: [
      "Ajinkya Delvi specializes in active thermal management, cooling loop design, and electrical efficiency tuning across high-voltage powertrain systems and battery packs for AutoNxt electric tractor platforms.",
      "He designs high-capacity closed-loop liquid cooling circuits that maintain motor controllers and lithium battery modules within optimal temperature thresholds, even during extreme 50°C Indian summer field conditions.",
      "Through computational fluid dynamics (CFD) modeling and field validation, Ajinkya prevents powertrain thermal derating, ensuring relentless peak torque delivery across demanding full-day commercial agricultural cycles."
    ],
    linkedinUrl: "https://www.linkedin.com/company/autonxt-automation",
    highlights: [
      "Designed Closed-Loop Thermal Balancing for Heavy Agricultural Loads",
      "Validated Continuous Maximum Torque Operation at 50°C Ambient Heat",
      "Optimized Inverter Heat Sink Geometries via Advanced CFD Simulations"
    ]
  },
  {
    slug: "khushpreet-singh",
    name: "Khushpreet Singh",
    role: "Testing & Validation Engineer",
    category: "manufacturing",
    categoryLabel: "Plant & Manufacturing",
    email: "khushpreet@autonxt.in",
    img: "/images/team/team/khushpreet.jpg",
    objectPosition: "50% 16%",
    location: "Thane, Maharashtra, India",
    expertise: ["Field Durability", "Vibration & Stress Testing", "ARAI Homologation", "Environmental Validation"],
    shortBio: "Durability engineer leading multi-terrain field trials, environmental stress tests, and ARAI homologation mandates.",
    fullBio: [
      "Khushpreet Singh directs accelerated testing, durability validation, and field certification protocols at AutoNxt Automation, guaranteeing the uncompromised reliability and safety of every production electric tractor.",
      "He subjects vehicle prototypes to grueling multi-axis vibration rigs, deep mud and water submersion tests, high-voltage interlock checks, and thousands of hours of continuous plowing across aggressive soil profiles.",
      "Khushpreet leads regulatory certification and homologation compliance under AIS-004 and AIS-048 standards, ensuring that every tractor engineered by AutoNxt delivers world-class build quality and farmer confidence."
    ],
    linkedinUrl: "https://www.linkedin.com/company/autonxt-automation",
    highlights: [
      "Executed 10,000+ Hours of Harsh Soil Accelerated Durability Trials",
      "Led Ingress Protection (IP67) & Extreme Submersion Verification",
      "Ensured Full Homologation Compliance with AIS-004 & AIS-048 Standards"
    ]
  },
  {
    slug: "swapnesh-jahagirdar",
    name: "Swapnesh Jahagirdar",
    role: "Business Development Manager",
    category: "sales",
    categoryLabel: "Sales Team",
    email: "swapnesh@autonxt.in",
    img: "/images/team/team/swapnesh.jpg",
    objectPosition: "50% 16%",
    location: "Thane / Mumbai, India",
    expertise: ["Commercial Strategy", "Dealer Network Expansion", "Corporate Partnerships", "Fleet Sales"],
    shortBio: "Commercial growth leader driving pan-India dealer network onboarding, fleet partnerships, and customer financing.",
    fullBio: [
      "Swapnesh Jahagirdar leads commercial partnerships, institutional client engagements, and dealership network onboarding at AutoNxt Automation, accelerating nationwide market adoption of high-performance electric tractors.",
      "He collaborates closely with agribusiness conglomerates, agricultural universities, farm equipment channel partners, and government procurement agencies to establish robust distribution and regional service networks.",
      "Swapnesh structures innovative customer financing models and operational lease solutions that maximize return on investment for smallholder farmers and commercial fleets, making sustainable clean mobility accessible to all."
    ],
    linkedinUrl: "https://www.linkedin.com/company/autonxt-automation",
    highlights: [
      "Spearheads Pan-India Dealership Onboarding & Training Programs",
      "Established Pilot Partnerships with Key Agribusiness Leaders",
      "Drives Institutional Fleet Procurement & Subsidy Frameworks"
    ]
  },
  {
    slug: "iv-rao",
    name: "IV-Rao",
    role: "Senior Advisor",
    category: "advisors",
    categoryLabel: "Strategic Advisory Board",
    email: "ivrao@autonxt.in",
    img: "/images/team/team/iv-rao.jpg",
    objectPosition: "50% 18%",
    location: "New Delhi / Thane, India",
    expertise: ["Automotive R&D Strategy", "EV Policy & Standards", "Powertrain Industrialization", "Global OEM Best Practices"],
    shortBio: "Former Maruti Suzuki R&D Executive Director guiding AutoNxt in automotive architecture and mass-market scalability.",
    fullBio: [
      "Mr. I.V. Rao is a luminary in the Indian automotive industry with over 40 years of pioneering engineering leadership, having served as Executive Director and Head of Engineering & R&D at Maruti Suzuki India.",
      "Currently a Senior Distinguished Fellow at TERI, Mr. Rao advises AutoNxt Automation on vehicle systems validation, component localization, homologation compliance, and resilient Tier-1 automotive supply ecosystems.",
      "His strategic mentorship empowers AutoNxt to apply automotive-grade engineering rigor, accelerate vehicle validation, and scale high-torque electric tractor platforms across domestic and global agricultural markets."
    ],
    linkedinUrl: "https://www.linkedin.com/company/autonxt-automation",
    highlights: [
      "Former Executive Director - R&D, Maruti Suzuki India",
      "Senior Distinguished Fellow at TERI (The Energy and Resources Institute)",
      "Over 40 Years of Pioneering Leadership in Automotive Innovation"
    ]
  },
  {
    slug: "ashish-mehta",
    name: "Ashish Mehta",
    role: "Financial Advisor",
    category: "advisors",
    categoryLabel: "Strategic Advisory Board",
    email: "ashish@autonxt.in",
    img: "/images/team/team/ashish.jpg",
    objectPosition: "50% 16%",
    location: "Mumbai / Thane, India",
    expertise: ["Financial Governance", "Tier-1 Vendor Supply Ecosystems", "Operational Sourcing", "Capital Efficiency"],
    shortBio: "Automotive supply chain veteran advising on capital efficiency, global procurement, and vendor cost structures.",
    fullBio: [
      "Ashish Mehta advises AutoNxt Automation on financial structuring, global supply chain governance, capital allocation, and strategic procurement for sustainable commercial manufacturing scale.",
      "With decades of executive leadership across Tier-1 automotive manufacturing and industrial supply networks, Ashish guides AutoNxt in optimizing bill-of-materials costs while securing premium automotive-grade components.",
      "His strategic financial oversight and direct-vendor sourcing frameworks support AutoNxt's rapid manufacturing ramp-up, high component localization, and long-term financial margin resilience."
    ],
    linkedinUrl: "https://www.linkedin.com/company/autonxt-automation",
    highlights: [
      "Decades of Executive Leadership in Automotive Sourcing & Finance",
      "Architect of Cost-Optimized Direct-Vendor Supply Chains",
      "Drives High Component Localization & Financial Margin Resilience"
    ]
  },
  {
    slug: "swadeep-pillarisetti",
    name: "Swadeep Pillarisetti",
    role: "Strategy Consultant",
    category: "advisors",
    categoryLabel: "Strategic Advisory Board",
    email: "swadeep@autonxt.in",
    img: "/images/team/team/swadeep.jpg",
    objectPosition: "50% 16%",
    location: "Bengaluru / Thane, India",
    expertise: ["Venture Capital Strategy", "International Go-to-Market", "DeepTech Scaling", "Corporate Governance"],
    shortBio: "Global venture advisor accelerating product-market fit, international market entry, and high-impact partnerships.",
    fullBio: [
      "Swadeep Pillarisetti provides strategic counsel to AutoNxt Automation on venture capital dynamics, international market entry, corporate governance, and high-impact commercial partnerships.",
      "With an extensive track record as a DeepTech investor and corporate growth advisor, Swadeep helps align AutoNxt's commercial roadmap with global sustainability trends and clean agricultural transformation.",
      "He actively mentors executive leadership on strategic capital structuring, enterprise sales pipelines, and technology scaling, ensuring AutoNxt's mission achieves lasting global market impact."
    ],
    linkedinUrl: "https://www.linkedin.com/company/autonxt-automation",
    highlights: [
      "Accomplished DeepTech Angel & Venture Capital Investor",
      "Advises on International Market Entry & Commercialization Frameworks",
      "Mentors Executive Leadership on Growth, Strategy, and Governance"
    ]
  }
];

// Multilingual translations for team members across all supported languages
const TEAM_TRANSLATIONS: Record<string, Record<string, Partial<TeamMemberProfile>>> = {
  hi: {
    "kaustubh-dhonde": {
      name: "कौस्तुभ धोंडे",
      role: "मुख्य कार्यकारी अधिकारी (CEO)",
      categoryLabel: "कार्यकारी नेतृत्व",
      location: "ठाणे, महाराष्ट्र, भारत",
      shortBio: "अभिनव इलेक्ट्रिक वाहन समाधानों के साथ कृषि में क्रांति लाने के AutoNxt के मिशन का नेतृत्व करने वाले दूरदर्शी लीडर।",
      fullBio: [
        "कौस्तुभ धोंडे AutoNxt Automation के संस्थापक और मुख्य कार्यकारी अधिकारी हैं, जो भारत के पहले स्वदेशी उच्च-टॉर्क इलेक्ट्रिक और स्वायत्त ट्रैक्टर प्लेटफॉर्म का नेतृत्व कर रहे हैं।",
        "रोबोटिक्स, एम्बेडेड इंटेलिजेंस और टिकाऊ कृषि गतिशीलता के प्रति गहरी लगन के साथ, कौस्तुभ ने किसानों के लिए डीजल लागत को समाप्त करने और कृषि उत्पादकता बढ़ाने के लिए AutoNxt की स्थापना की।",
        "उनके नेतृत्व में, AutoNxt ने ग्राउंडब्रेकिंग ईवी पावरट्रेन आर्किटेक्चर, हाई-वोल्टेज बैटरी सुरक्षा और एआई-संचालित स्वायत्त तकनीक का निर्माण किया है।"
      ]
    },
    "pankaj-goyal": {
      name: "पंकज गोयल",
      role: "सह-संस्थापक और सीओओ (COO)",
      categoryLabel: "कार्यकारी नेतृत्व",
      location: "ठाणे, महाराष्ट्र, भारत",
      shortBio: "सुलभ निष्पादन और विकास सुनिश्चित करने वाले रणनीतिकार, दैनिक संचालन और साझेदारियों की देखरेख करते हैं।",
      fullBio: [
        "पंकज गोयल AutoNxt Automation के सह-संस्थापक और मुख्य परिचालन अधिकारी हैं। वे कॉर्पोरेट संचालन, विनिर्माण विस्तार और व्यावसायिक तैनाती का नेतृत्व करते हैं।",
        "व्यापक औद्योगिक अनुभव के साथ, पंकज ने गुणवत्ता प्रबंधन और टियर-1 वेंडर आपूर्ति श्रृंखलाएं स्थापित की हैं जो ऑटोमोटिव-ग्रेड विश्वसनीयता सुनिश्चित करती हैं।"
      ]
    },
    "gaurav-borkar": {
      name: "गौरव बोरकर",
      role: "चीफ फाइनेंशियल ऑफिसर (CFO)",
      categoryLabel: "वित्त एवं रणनीति",
      qualification: "एमबीए (फाइनेंस) | सीएफए लेवल I",
      location: "ठाणे, महाराष्ट्र, भारत",
      shortBio: "पूंजी आवंटन, कॉर्पोरेट गवर्नेंस, निवेशक संबंध और वित्तीय योजना का नेतृत्व करने वाले रणनीतिक वित्त लीडर।",
      fullBio: [
        "गौरव बोरकर AutoNxt Automation में मुख्य वित्तीय अधिकारी (CFO) हैं, जो कंपनी की वित्तीय रणनीति, पूंजी आवंटन, कॉर्पोरेट प्रशासन और दीर्घकालिक वित्तीय योजनाओं का नेतृत्व करते हैं।",
        "फाइनेंस में एमबीए और सीएफए लेवल I योग्यता के साथ, गौरव स्केलेबल वित्तीय सिस्टम बनाने, ट्रेजरी प्रबंधन को अनुकूलित करने और मजबूत आंतरिक नियंत्रण स्थापित करने में व्यापक विशेषज्ञता लाते हैं।",
        "वे रणनीतिक विस्तार का मूल्यांकन करने, धन जुटाने के मील के पत्थरों का समर्थन करने और स्थायी मूल्य सृजन के लिए अनुशासित कॉर्पोरेट प्रशासन के साथ काम करते हैं।"
      ]
    },
    "amit-kumar-shivhare": {
      name: "अमित कुमार शिवहरे",
      role: "हेड – अकाउंट्स एंड फाइनेंस (Head – Accounts & Finance)",
      categoryLabel: "वित्त एवं लेखा",
      qualification: "एमबीए (फाइनेंस) | 15+ वर्षों का अनुभव",
      location: "ठाणे, महाराष्ट्र, भारत",
      shortBio: "ईवी विनिर्माण, प्रौद्योगिकी और बुनियादी ढांचे में वित्तीय रणनीति, शासन और व्यावसायिक प्रदर्शन का नेतृत्व करने वाले वरिष्ठ वित्त लीडर।",
      fullBio: [
        "ईवी/ऑटोमोबाइल विनिर्माण, प्रौद्योगिकी और प्रकाशन, बुनियादी ढांचे और रियल एस्टेट में वित्तीय रणनीति, शासन और व्यावसायिक प्रदर्शन को आगे बढ़ाने में 15+ वर्षों के प्रगतिशील अनुभव के साथ वरिष्ठ लेखा और वित्त लीडर।",
        "वर्तमान में एक वेंचर-वित्त पोषित ईवी स्टार्टअप के संपूर्ण वित्त कार्य का नेतृत्व कर रहे हैं, जिसमें वित्तीय योजना, बजट, नकदी प्रवाह और फंड प्रबंधन, लागत, कराधान, अनुपालन, एमआईएस, ऑडिट और प्रबंधन रिपोर्टिंग की जिम्मेदारी शामिल है।",
        "मजबूत वित्तीय नियंत्रण स्थापित करने, कार्यशील पूंजी और लागत को अनुकूलित करने, प्रक्रियाओं को मजबूत करने और निवेशक-तैयार वित्तीय अंतर्दृष्टि प्रदान करने की सिद्ध क्षमता। सतत विकास और लाभप्रदता का समर्थन करने के लिए मजबूत वित्तीय सूझबूझ, परिचालन नेतृत्व और डेटा-संचालित निर्णय लेने वाले वरिष्ठ प्रबंधन के रणनीतिक व्यापार भागीदार।"
      ]
    },
    "pratik": {
      name: "प्रतीक",
      role: "हेड ऑफ एफपीएंडए (Head of FP&A)",
      categoryLabel: "वित्त एवं लेखा",
      qualification: "चार्टर्ड अकाउंटेंट (CA)",
      location: "ठाणे, महाराष्ट्र, भारत",
      shortBio: "कॉर्पोरेट वित्त, एफपीएंडए, वित्तीय मॉडलिंग और रणनीतिक वित्तीय शासन का नेतृत्व करने वाले चार्टर्ड अकाउंटेंट।",
      fullBio: [
        "कॉर्पोरेट वित्त, एफपीएंडए, वर्चुअल सीएफओ सलाहकार, वित्तीय मॉडलिंग, फंड जुटाने, व्यापार रणनीति और प्रदर्शन प्रबंधन में गहन विशेषज्ञता वाले चार्टर्ड अकाउंटेंट।",
        "स्केलेबल वित्तीय ढांचे स्थापित करने, नकदी प्रवाह दृश्यता को मजबूत करने और रणनीतिक विकास को आगे बढ़ाने के लिए नेतृत्व के साथ साझेदारी करते हैं। पूर्वानुमान, बजट और रिपोर्टिंग के माध्यम से जटिल डेटा को कार्रवाई योग्य अंतर्दृष्टि में अनुवाद करते हैं।",
        "वित्तीय मॉडलिंग और उचित परिश्रम का नेतृत्व करके 15 मिलियन अमरीकी डालर की पूंजी जुटाने में सफलतापूर्वक योगदान दिया। सतत विकास और लाभप्रदता का समर्थन करने के लिए मासिक एमआईएस रिपोर्टिंग और तरलता अनुकूलन का निर्देशन करते हैं।"
      ]
    },
    "hari-kishan": {
      name: "हरि किशन",
      role: "प्लांट हेड",
      categoryLabel: "प्लांट और विनिर्माण",
      location: "ठाणे, महाराष्ट्र, भारत",
      shortBio: "संयंत्र संचालन की देखरेख करते हैं, प्रत्येक निर्मित ट्रैक्टर में गुणवत्ता और दक्षता सुनिश्चित करते हैं।"
    },
    "sudipto-deb": {
      name: "सुदीप्तो देब",
      role: "लीड पावर ट्रेन इंजीनियर",
      categoryLabel: "पावरट्रेन और बैटरी",
      location: "ठाणे, महाराष्ट्र, भारत",
      shortBio: "पावर इलेक्ट्रॉनिक्स विशेषज्ञ, हमारे ट्रैक्टरों को शक्ति देने वाले इलेक्ट्रिकल सिस्टम को डिजाइन और अनुकूलित करते हैं।"
    },
    "vamsi-atluri": {
      name: "वामसी अट्लुरी",
      role: "लीड मैकेनिकल डिजाइन इंजीनियर",
      categoryLabel: "चेसिस और मैकेनिकल इंजीनियरिंग",
      location: "ठाणे, महाराष्ट्र, भारत",
      shortBio: "विविध कृषि आवश्यकताओं के लिए मजबूत और कुशल ट्रैक्टर संरचनाएं तैयार करने वाले मैकेनिकल डिजाइन इनोवेटर।"
    },
    "dr-dharmateja-adapa": {
      name: "डॉ. धर्मतेजा अदापा",
      role: "लीड रोबोटिक्स इंजीनियर",
      categoryLabel: "रोबोटिक्स और सॉफ्टवेयर",
      location: "ठाणे, महाराष्ट्र, भारत",
      shortBio: "सहज और विश्वसनीय ट्रैक्टर प्रदर्शन के लिए स्मार्ट तकनीकों को एकीकृत करने पर केंद्रित स्वचालन विशेषज्ञ।"
    },
    "tejesh-madireddy": {
      name: "तेजेश मादिरेड्डी",
      role: "लीड एम्बेडेड इंजीनियर",
      categoryLabel: "रोबोटिक्स और सॉफ्टवेयर",
      location: "ठाणे, महाराष्ट्र, भारत",
      shortBio: "विश्वसनीय और कुशल हार्डवेयर-सॉफ्टवेयर एकीकरण सुनिश्चित करते हुए एम्बेडेड सिस्टम विकास का नेतृत्व करते हैं।"
    },
    "aswanth-mulupuri": {
      name: "अश्वंथ मुलुपुरी",
      role: "रोबोटिक्स और ऑटोमेशन इंजीनियर",
      categoryLabel: "रोबोटिक्स और सॉफ्टवेयर",
      location: "ठाणे, महाराष्ट्र, भारत",
      shortBio: "स्वायत्त ट्रैक्टर संचालन के लिए उन्नत नियंत्रण प्रणाली विकसित करने वाले स्वचालन विशेषज्ञ।"
    },
    "ajinkya-delvi": {
      name: "अजिंक्य डेलवी",
      role: "पावर ट्रेन इंजीनियर",
      categoryLabel: "पावरट्रेन और बैटरी",
      location: "ठाणे, महाराष्ट्र, भारत",
      shortBio: "पावर ट्रेन सिस्टम पर काम करते हुए प्रदर्शन और ऊर्जा दक्षता का अनुकूलन करते हैं।"
    },
    "khushpreet-singh": {
      name: "खुशप्रीत सिंह",
      role: "परीक्षण और सत्यापन इंजीनियर",
      categoryLabel: "प्लांट और विनिर्माण",
      location: "ठाणे, महाराष्ट्र, भारत",
      shortBio: "कड़े परीक्षण और सत्यापन के लिए जिम्मेदार, उत्पादों की विश्वसनीयता और सुरक्षा की गारंटी देते हैं।"
    },
    "swapnesh-jahagirdar": {
      name: "स्वप्नेश जहागीरदार",
      role: "बिजनेस डेवलपमेंट मैनेजर",
      categoryLabel: "सेल्स टीम",
      location: "ठाणे / मुंबई, भारत",
      shortBio: "कंपनी के विस्तार में तेजी लाने के लिए नए अवसरों की पहचान और संबंधों को बढ़ावा देने पर केंद्रित।"
    },
    "iv-rao": {
      name: "आईवी-राव",
      role: "वरिष्ठ सलाहकार",
      categoryLabel: "रणनीतिक सलाहकार बोर्ड",
      location: "नई दिल्ली / ठाणे, भारत",
      shortBio: "मारुति सुजुकी के पूर्व कार्यकारी सलाहकार और इंजीनियरिंग प्रमुख। ऑटोमोटिव आर्किटेक्चर और सिस्टम इंजीनियरिंग में AutoNxt का मार्गदर्शन।"
    },
    "ashish-mehta": {
      name: "आशीष मेहता",
      role: "वित्तीय सलाहकार",
      categoryLabel: "रणनीतिक सलाहकार बोर्ड",
      location: "मुंबई / ठाणे, भारत",
      shortBio: "पूंजी आवंटन, वेंचर स्केल और टिकाऊ वाणिज्यिक विस्तार पर सलाह देने वाले अनुभवी वित्तीय रणनीतिकार।"
    },
    "swadeep-pillarisetti": {
      name: "स्वादीप पिल्लारीसेट्टी",
      role: "रणनीति सलाहकार",
      categoryLabel: "रणनीतिक सलाहकार बोर्ड",
      location: "बेंगलुरु / ठाणे, भारत",
      shortBio: "उत्पाद-बाजार अनुकूलता, उद्यम बिक्री पाइपलाइनों और उच्च-प्रभाव वाली साझेदारियों को गति देने में विशेषज्ञ।"
    }
  },
  mr: {
    "kaustubh-dhonde": {
      name: "कौस्तुभ धोंडे",
      role: "मुख्य कार्यकारी अधिकारी (CEO)",
      categoryLabel: "कार्यकारी नेतृत्व",
      location: "ठाणे, महाराष्ट्र, भारत",
      shortBio: "अभिनव इलेक्ट्रिक वाहन सोल्यूशन्ससह शेतीमध्ये क्रांती घडवून आणण्याच्या AutoNxt च्या मिशनचे नेतृत्व करणारे दूरदर्शी लीडर.",
      fullBio: [
        "कौस्तुभ धोंडे हे AutoNxt Automation चे संस्थापक आणि मुख्य कार्यकारी अधिकारी आहेत, जे भारताच्या पहिल्या स्वदेशी हाय-टॉर्क इलेक्ट्रिक आणि स्वायत्त ट्रॅक्टर प्लॅटफॉर्मचे नेतृत्व करत आहेत.",
        "रोबोटिक्स, एम्बेडेड इंटेलिजन्स आणि शाश्वत कृषी गतिशीलतेची आवड असलेले कौस्तुभ यांनी शेतकऱ्यांचा डिझेल खर्च वाचवण्यासाठी आणि शेतीची उत्पादकता वाढवण्यासाठी AutoNxt ची स्थापना केली.",
        "त्यांच्या नेतृत्वाखाली AutoNxt ने ग्राउंडब्रेकिंग ईव्ही पॉवरट्रेन आणि एआय-सक्षम स्वायत्त तंत्रज्ञानाची यशस्वी निर्मिती केली आहे."
      ]
    },
    "pankaj-goyal": {
      name: "पंकज गोयल",
      role: "सह-संस्थापक आणि सीओओ (COO)",
      categoryLabel: "कार्यकारी नेतृत्व",
      location: "ठाणे, महाराष्ट्र, भारत",
      shortBio: "अखंड अंमलबजावणी आणि विकास सुनिश्चित करणारे धोरणकर्ते, दैनंदिन कामकाज आणि भागीदारींवर देखरेख करतात."
    },
    "gaurav-borkar": {
      name: "गौरव बोरकर",
      role: "चीफ फायनान्शियल ऑफिसर (CFO)",
      categoryLabel: "वित्त आणि धोरण",
      qualification: "एमबीए (फायनान्स) | सीएफए लेव्हल I",
      location: "ठाणे, महाराष्ट्र, भारत",
      shortBio: "भांडवल वाटप, कॉर्पोरेट गव्हर्नन्स, गुंतवणूकदार संबंध आणि वित्तीय नियोजनाचे नेतृत्व करणारे रणनीतिक वित्त नेते.",
      fullBio: [
        "गौरव बोरकर हे AutoNxt Automation मध्ये मुख्य आर्थिक अधिकारी (CFO) आहेत, जे कंपनीची आर्थिक रणनीती, भांडवल वाटप, कॉर्पोरेट गव्हर्नन्स आणि दीर्घकालीन वित्तीय योजनांचे नेतृत्व करतात.",
        "फायनान्समध्ये एमबीए आणि सीएफए लेव्हल I सह, गौरव स्केलेबल वित्तीय प्रणाली उभारण्यात, ट्रेजरी व्यवस्थापन आणि मजबूत अंतर्गत नियंत्रणे स्थापित करण्यात सखोल अनुभव आणतात.",
        "ते धोरणात्मक विस्ताराचे मूल्यांकन करण्यासाठी, निधी उभारणी आणि शाश्वत संस्थात्मक मूल्यासाठी नेतृत्व संघासोबत कार्यरत आहेत."
      ]
    },
    "amit-kumar-shivhare": {
      name: "अमित कुमार शिवहरे",
      role: "हेड – अकाउंट्स अँड फायनान्स (Head – Accounts & Finance)",
      categoryLabel: "वित्त आणि लेखा",
      qualification: "एमबीए (फायनान्स) | 15+ वर्षांचा अनुभव",
      location: "ठाणे, महाराष्ट्र, भारत",
      shortBio: "ईव्ही उत्पादन, तंत्रज्ञान आणि पायाभूत सुविधांमध्ये आर्थिक रणनीती आणि व्यावसायिक कामगिरीचे नेतृत्व करणारे ज्येष्ठ वित्त नेते.",
      fullBio: [
        "ईव्ही/ऑटोमोबाईल उत्पादन, तंत्रज्ञान आणि प्रकाशन, पायाभूत सुविधा आणि रिअल इस्टेटमध्ये आर्थिक रणनीती, गव्हर्नन्स आणि व्यवसाय कामगिरीला चालना देण्याचा 15+ वर्षांचा प्रदीर्घ अनुभव असलेले वरिष्ठ लेखा आणि वित्त नेते.",
        "सध्या व्हेंचर-फंडेड ईव्ही स्टार्टअपच्या संपूर्ण वित्त कार्याचे नेतृत्व करत आहेत, ज्यामध्ये वित्तीय नियोजन, बजेटिंग, रोख प्रवाह आणि निधी व्यवस्थापन, कॉस्टिंग, करप्रणाली, अनुपालन, एमआयएस, ऑडिट आणि व्यवस्थापन अहवालाची जबाबदारी आहे.",
        "मजबूत वित्तीय नियंत्रणे स्थापित करणे, खेळते भांडवल आणि खर्च ऑप्टिमाइझ करणे आणि गुंतवणूकदार-सज्ज आर्थिक अंतर्दृष्टी प्रदान करण्याची सिद्ध क्षमता. शाश्वत वाढ आणि नफ्याला समर्थन देण्यासाठी वरिष्ठ व्यवस्थापनाचे रणनीतिक व्यवसाय भागीदार."
      ]
    },
    "pratik": {
      name: "प्रतीक",
      role: "हेड ऑफ एफपीअँडए (Head of FP&A)",
      categoryLabel: "वित्त आणि लेखा",
      qualification: "चार्टर्ड अकाउंटंट (CA)",
      location: "ठाणे, महाराष्ट्र, भारत",
      shortBio: "कॉर्पोरेट वित्त, एफपीअँडए, वित्तीय मॉडेलिंग आणि धोरणात्मक वित्तीय प्रशासनाचे नेतृत्व करणारे चार्टर्ड अकाउंटंट.",
      fullBio: [
        "कॉर्पोरेट फायनान्स, एफपीअँडए, व्हर्च्युअल सीएफओ सल्लागार, वित्तीय मॉडेलिंग, निधी उभारणी, व्यवसाय धोरण आणि कामगिरी व्यवस्थापनातील सखोल कौशल्य असलेले चार्टर्ड अकाउंटंट.",
        "मापनक्षम वित्तीय चौकट स्थापित करण्यासाठी, रोख प्रवाह दृश्यमानता मजबूत करण्यासाठी आणि धोरणात्मक वाढीला चालना देण्यासाठी नेतृत्वासोबत भागीदारी करतात. अंदाज, अंदाजपत्रक आणि अहवालाद्वारे कृतीयोग्य अंतर्दृष्टी प्रदान करतात.",
        "आर्थिक मॉडेलिंगचे नेतृत्व करून USD 15 दशलक्ष भांडवल उभारणीत यशस्वीरित्या योगदान दिले. शाश्वत वाढ आणि नफ्याला समर्थन देण्यासाठी मासिक एमआयएस अहवाल आणि तरलता ऑप्टिमायझेशनचे व्यवस्थापन करतात."
      ]
    },
    "hari-kishan": {
      name: "हरी किशन",
      role: "प्लॅन्ट हेड",
      categoryLabel: "प्लॅन्ट आणि मॅन्युफॅक्चरिंग",
      location: "ठाणे, महाराष्ट्र, भारत",
      shortBio: "प्लॅन्ट ऑपरेशन्सची देखरेख करतात, प्रत्येक तयार होणाऱ्या ट्रॅक्टरमध्ये गुणवत्ता आणि कार्यक्षमता सुनिश्चित करतात."
    },
    "sudipto-deb": {
      name: "सुदीप्तो देब",
      role: "लीड पॉवर ट्रेन इंजिनिअर",
      categoryLabel: "पॉवरट्रेन आणि बॅटरी",
      location: "ठाणे, महाराष्ट्र, भारत",
      shortBio: "पॉवर इलेक्ट्रॉनिक्स तज्ज्ञ, आमच्या ट्रॅक्टर्सना ऊर्जा देणाऱ्या इलेक्ट्रिकल सिस्टिम्स डिझाइन आणि ऑप्टिमाइझ करतात."
    },
    "vamsi-atluri": {
      name: "वामसी अट्लुरी",
      role: "लीड मेकॅनिकल डिझाइन इंजिनिअर",
      categoryLabel: "चेसिस आणि मेकॅनिकल इंजिनिअरिंग",
      location: "ठाणे, महाराष्ट्र, भारत",
      shortBio: "विविध शेती गरजांसाठी मजबूत आणि कार्यक्षम ट्रॅक्टर संरचना तयार करणारे मेकॅनिकल डिझाइन इनोव्हेटर."
    },
    "dr-dharmateja-adapa": {
      name: "डॉ. धर्मतेजा अदापा",
      role: "लीड रोबोटिक्स इंजिनिअर",
      categoryLabel: "रोबोटिक्स आणि सॉफ्टवेअर",
      location: "ठाणे, महाराष्ट्र, भारत",
      shortBio: "ट्रॅक्टरच्या उत्कृष्ट कामगिरीसाठी स्मार्ट तंत्रज्ञान समाविष्ट करण्यावर भर देणारे ऑटोमेशन तज्ज्ञ."
    },
    "tejesh-madireddy": {
      name: "तेजेश मादिरेड्डी",
      role: "लीड एम्बेडेड इंजिनिअर",
      categoryLabel: "रोबोटिक्स आणि सॉफ्टवेअर",
      location: "ठाणे, महाराष्ट्र, भारत",
      shortBio: "विश्वासार्ह आणि कार्यक्षम हार्डवेअर-सॉफ्टवेअर एकत्रीकरण सुनिश्चित करून एम्बेडेड सिस्टिम्स विकासाचे नेतृत्व करतात."
    },
    "aswanth-mulupuri": {
      name: "अश्वंथ मुलुपुरी",
      role: "रोबोटिक्स आणि ऑटोमेशन इंजिनिअर",
      categoryLabel: "रोबोटिक्स आणि सॉफ्टवेअर",
      location: "ठाणे, महाराष्ट्र, भारत",
      shortBio: "स्वायत्त ट्रॅक्टर चालनासाठी प्रगत नियंत्रण प्रणाली विकसित करणारे ऑटोमेशन स्पेशालिस्ट."
    },
    "ajinkya-delvi": {
      name: "अजिंक्य डेलवी",
      role: "पॉवर ट्रेन इंजिनिअर",
      categoryLabel: "पॉवरट्रेन आणि बॅटरी",
      location: "ठाणे, महाराष्ट्र, भारत",
      shortBio: "पॉवर ट्रेन सिस्टिम्सवर काम करत कार्यक्षमता आणि ऊर्जा बचतीचे अनुकूलन करतात."
    },
    "khushpreet-singh": {
      name: "खुशप्रीत सिंग",
      role: "टेस्टिंग आणि व्हॅलिडेशन इंजिनिअर",
      categoryLabel: "प्लॅन्ट आणि मॅन्युफॅक्चरिंग",
      location: "ठाणे, महाराष्ट्र, भारत",
      shortBio: "कठोर चाचणी आणि प्रमाणीकरणासाठी जबाबदार, उत्पादनांची विश्वासार्हता आणि सुरक्षितता हमी देतात."
    },
    "swapnesh-jahagirdar": {
      name: "स्वप्नेश जहागीरदार",
      role: "बिझनेस डेव्हलपमेंट मॅनेजर",
      categoryLabel: "सेल्स टीम",
      location: "ठाणे / मुंबई, भारत",
      shortBio: "कंपनीचा विस्तार गतिमान करण्यासाठी नवीन व्यावसायिक संधी शोधणे आणि संबंध वृद्धिंगत करणे यावर लक्ष केंद्रित."
    },
    "iv-rao": {
      name: "आयव्ही-राव",
      role: "ज्येष्ठ सल्लागार",
      categoryLabel: "स्ट्रॅटेजिक सल्लागार मंडळ",
      location: "नवी दिल्ली / ठाणे, भारत",
      shortBio: "मारुती सुझुकीचे माजी कार्यकारी सल्लागार आणि अभियांत्रिकी प्रमुख. ऑटोमोटिव्ह आर्किटेक्चर आणि सिस्टिम्स अभियांत्रिकीमध्ये AutoNxt ला मार्गदर्शन."
    },
    "ashish-mehta": {
      name: "आशिष मेहता",
      role: "आर्थिक सल्लागार",
      categoryLabel: "स्ट्रॅटेजिक सल्लागार मंडळ",
      location: "मुंबई / ठाणे, भारत",
      shortBio: "भांडवल वाटप, व्हेंचर स्केल आणि शाश्वत व्यावसायिक विस्तारावर मार्गदर्शन करणारे अनुभवी वित्तीय रणनीतिकार."
    },
    "swadeep-pillarisetti": {
      name: "स्वादीप पिल्लारीसेट्टी",
      role: "स्ट्रॅटेजी कन्सल्टंट",
      categoryLabel: "स्ट्रॅटेजिक सल्लागार मंडळ",
      location: "बेंगळुरू / ठाणे, भारत",
      shortBio: "प्रॉडक्ट-मार्केट फिट, एंटरप्राइझ विक्री आणि उच्च-प्रभावी भागीदारींना गती देणारे जागतिक रणनीती तज्ज्ञ."
    }
  },
  te: {
    "kaustubh-dhonde": {
      name: "కౌస్తుభ్ ధోండే",
      role: "చీఫ్ ఎగ్జిక్యూటివ్ ఆఫీసర్ (CEO)",
      categoryLabel: "ఎగ్జిక్యూటివ్ నాయకత్వం",
      location: "థానే, మహారాష్ట్ర, భారతదేశం",
      shortBio: "వినూత్న ఎలక్ట్రిక్ వాహన పరిష్కారాలతో వ్యవసాయంలో విప్లవాత్మక మార్పులు తీసుకురావాలనే లక్ష్యంతో ముందుకెళ్తున్న దూరదృష్టి గల నాయకుడు.",
      fullBio: [
        "కౌస్తుభ్ ధోండే AutoNxt Automation వ్యవస్థాపకుడు మరియు CEO. భారతదేశపు మొట్టమొదటి హై-టార్క్ ఎలక్ట్రిక్ మరియు స్వయంప్రతిపత్త ట్రాక్టర్ ప్లాట్‌ఫామ్‌ను రూపొందించడంలో కీలక పాత్ర పోషిస్తున్నారు.",
        "రోబోటిక్స్, ఎంబెడెడ్ ఇంటెలిజెన్స్ మరియు సుస్థిర వ్యవసాయంపై అమితమైన ఆసక్తితో, రైతుల డీజిల్ ఖర్చులను తగ్గించడానికి AutoNxt ను స్థాపించారు."
      ]
    },
    "pankaj-goyal": {
      name: "పంకజ్ గోయల్",
      role: "కో-ఫౌండర్ & సీఓఓ (COO)",
      categoryLabel: "ఎగ్జిక్యూటివ్ నాయకత్వం",
      location: "థానే, మహారాష్ట్ర, భారతదేశం",
      shortBio: "సమర్థవంతమైన నిర్వహణ మరియు వృద్ధిని నిర్ధారిస్తూ రోజువారీ కార్యకలాపాలు మరియు భాగస్వామ్యాలను పర్యవేక్షిస్తారు."
    },
    "gaurav-borkar": {
      name: "గౌరవ్ బోర్కర్",
      role: "చీఫ్ ఫైనాన్షియల్ ఆఫీసర్ (CFO)",
      categoryLabel: "ఫైనాన్స్ & స్ట్రాటజీ",
      qualification: "ఫైనాన్స్‌లో ఎంబీఏ | CFA లెవల్ I",
      location: "థానే, మహారాష్ట్ర, భారతదేశం",
      shortBio: "కార్పొరేట్ పాలన, మూలధన కేటాయింపు, పెట్టుబడిదారుల సంబంధాలు మరియు ఆర్థిక ప్రణాళికలను నడిపించే వ్యూహాత్మక ఫైనాన్స్ లీడర్.",
      fullBio: [
        "గౌరవ్ బోర్కర్ AutoNxt Automation లో చీఫ్ ఫైనాన్షియల్ ఆఫీసర్ (CFO) గా కంపెనీ ఆర్థిక వ్యూహం, మూలధన కేటాయింపు మరియు దీర్ఘకాలిక ప్రణాళికలను నడిపిస్తున్నారు.",
        "ఫైనాన్స్‌లో ఎంబీఏ మరియు CFA లెవల్ I అర్హతతో, గౌరవ్ సమర్థవంతమైన ఆర్థిక వ్యవస్థలను నిర్మించడంలో మరియు అంతర్గత నియంత్రణలను బలోపేతం చేయడంలో అపార అనుభవం కలిగి ఉన్నారు.",
        "కంపెనీ విస్తరణ, నిధుల సమీకరణ మరియు స్థిరమైన విలువ సృష్టి కోసం ఆయన నాయకత్వ బృందంతో కలిసి పనిచేస్తున్నారు."
      ]
    },
    "amit-kumar-shivhare": {
      name: "అమిత్ కుమార్ శివహరే",
      role: "హెడ్ – అకౌంట్స్ & ఫైనాన్స్ (Head – Accounts & Finance)",
      categoryLabel: "ఫైనాన్స్ & అకౌంట్స్",
      qualification: "ఎంబీఏ (ఫైనాన్స్) | 15+ సంవత్సరాల అనుభవం",
      location: "థానే, మహారాష్ట్ర, భారతదేశం",
      shortBio: "ఈవీ తయారీ, సాంకేతికత మరియు మౌలిక సదుపాయాలలో ఆర్థిక వ్యూహం, పాలన మరియు వ్యాపార పనితీరును నడిపించే సీనియర్ ఫైనాన్స్ లీడర్.",
      fullBio: [
        "ఈవీ/ఆటోమొబైల్ తయారీ, సాంకేతికత, మౌలిక సదుపాయాలు మరియు రియల్ ఎస్టేట్ రంగాలలో ఆర్థిక వ్యూహం, పాలన మరియు వ్యాపార పనితీరును ముందుకు తీసుకెళ్లడంలో 15+ సంవత్సరాల అనుభవం కలిగిన సీనియర్ అకౌంట్స్ & ఫైనాన్స్ లీడర్.",
        "ప్రస్తుతం వెంచర్-ఫండెడ్ ఈవీ స్టార్టప్ యొక్క పూర్తి ఫైనాన్స్ విభాగానికి నాయకత్వం వహిస్తున్నారు; ఆర్థిక ప్రణాళిక, బడ్జెట్, నగదు ప్రవాహం మరియు నిధుల నిర్వహణ, వ్యయం, పన్నులు, చట్టబద్ధమైన నిబంధనలు, MIS మరియు ఆడిట్‌ల బాధ్యత వహిస్తారు.",
        "బలమైన ఆర్థిక నియంత్రణలను నెలకొల్పడం, నిర్వహణ మూలధనం మరియు ఖర్చులను ఆప్టిమైజ్ చేయడం మరియు పెట్టుబడిదారులకు నివేదికలను అందించడంలో నిరూపితమైన సామర్థ్యం. స్థిరమైన వృద్ధికి తోడ్పడే వ్యూహాత్మక వ్యాపార భాగస్వామి."
      ]
    },
    "pratik": {
      name: "ప్రతీక్",
      role: "హెడ్ ఆఫ్ FP&A (Head of FP&A)",
      categoryLabel: "ఫైనాన్స్ & అకౌంట్స్",
      qualification: "చార్టర్డ్ అకౌంటెంట్ (CA)",
      location: "థానే, మహారాష్ట్ర, భారతదేశం",
      shortBio: "కార్పొరేట్ ఫైనాన్స్, FP&A, ఫైనాన్షియల్ మోడలింగ్ మరియు వ్యూహాత్మక ఫైనాన్స్ గవర్నెన్స్‌కు నాయకత్వం వహిస్తున్న చార్టర్డ్ అకౌంటెంట్.",
      fullBio: [
        "కార్పొరేట్ ఫైనాన్స్, FP&A, వర్చువల్ CFO సలహా, ఫైనాన్షియల్ మోడలింగ్, నిధుల సమీకరణ మరియు వ్యాపార వ్యూహాలలో నైపుణ్యం కలిగిన చార్టర్డ్ అకౌంటెంట్.",
        "నగదు ప్రవాహం మరియు వ్యూహాత్మక వృద్ధిని పెంచడానికి నాయకత్వంతో కలిసి పని చేస్తారు. అంచనాలు మరియు బడ్జెట్ ద్వారా సంక్లిష్ట డేటాను వ్యాపార నిర్ణయాలుగా మార్చడంలో అనుభవం కలదు.",
        "ఫైనాన్షియల్ మోడలింగ్ మరియు డ్యూ డిలిజెన్స్ ద్వారా USD 15 మిలియన్ల మూలధన సమీకరణకు విజయవంతంగా సహకరించారు. స్థిరమైన వృద్ధి మరియు లాభదాయకతకు తోడ్పడేందుకు MIS రిపోర్టింగ్‌ను నడిపిస్తారు."
      ]
    },
    "hari-kishan": {
      name: "హరి కిషన్",
      role: "ప్లాంట్ హెడ్",
      categoryLabel: "ప్లాంట్ & తయారీ",
      location: "థానే, మహారాష్ట్ర, భారతదేశం",
      shortBio: "ప్లాంట్ కార్యకలాపాలను పర్యవేక్షిస్తూ, తయారయ్యే ప్రతి ట్రాక్టర్‌లో నాణ్యత మరియు సామర్థ్యాన్ని నిర్ధారిస్తారు."
    },
    "sudipto-deb": {
      name: "సుదీప్తో దేబ్",
      role: "లీడ్ పవర్ ట్రైన్ ఇంజనీర్",
      categoryLabel: "పవర్‌ట్రైన్ & బ్యాటరీ",
      location: "థానే, మహారాష్ట్ర, భారతదేశం",
      shortBio: "పవర్ ఎలక్ట్రానిక్స్ నిపుణుడు, మా ట్రాక్టర్లకు శక్తినిచ్చే ఎలక్ట్రికల్ సిస్టమ్స్‌ను రూపొందించడంలో మరియు మెరుగుపరచడంలో బాధ్యత వహిస్తారు."
    },
    "vamsi-atluri": {
      name: "వామ్సీ అట్లూరి",
      role: "లీడ్ మెకానికల్ డిజైన్ ఇంజనీర్",
      categoryLabel: "చాసిస్ & మెకానికల్ ఇంజనీరింగ్",
      location: "థానే, మహారాష్ట్ర, భారతదేశం",
      shortBio: "వివిధ వ్యవసాయ అవసరాల కోసం బలమైన మరియు సమర్థవంతమైన ట్రాక్టర్ నిర్మాణాలను రూపొందించే మెకానికల్ డిజైన్ ఇన్నోవేటర్."
    },
    "dr-dharmateja-adapa": {
      name: "డా. ధర్మతేజ అడపా",
      role: "లీడ్ రోబోటిక్స్ ఇంజనీర్",
      categoryLabel: "రోబోటిక్స్ & సాఫ్ట్‌వేర్",
      location: "థానే, మహారాష్ట్ర, భారతదేశం",
      shortBio: "ట్రాక్టర్ యొక్క అత్యుత్తమ పనితీరు కోసం స్మార్ట్ టెక్నాలజీలను ఏకీకృతం చేయడంలో ప్రత్యేకత కలిగిన ఆటోమేషన్ నిపుణుడు."
    },
    "tejesh-madireddy": {
      name: "తేజేష్ మాదిరెడ్డి",
      role: "లీడ్ ఎంబెడెడ్ ఇంజనీర్",
      categoryLabel: "రోబోటిక్స్ & సాఫ్ట్‌వేర్",
      location: "థానే, మహారాష్ట్ర, భారతదేశం",
      shortBio: "విశ్వసనీయ హార్డ్‌వేర్-సాఫ్ట్‌వేర్ సమన్వయాన్ని నిర్ధారిస్తూ ఎంబెడెడ్ సిస్టమ్స్ అభివృద్ధిని నడిపిస్తారు."
    },
    "aswanth-mulupuri": {
      name: "అశ్వంత్ ములుపూరి",
      role: "రోబోటిక్స్ & ఆటోమేషన్ ఇంజనీర్",
      categoryLabel: "రోబోటిక్స్ & సాఫ్ట్‌వేర్",
      location: "థానే, మహారాష్ట్ర, భారతదేశం",
      shortBio: "స్వయంప్రతిపత్త ట్రాక్టర్ల నిర్వహణ కోసం అధునాతన నియంత్రణ వ్యవస్థలను అభివృద్ధి చేసే ఆటోమేషన్ స్పెషలిస్ట్."
    },
    "ajinkya-delvi": {
      name: "అజింక్య దెల్వి",
      role: "పవర్ ట్రైన్ ఇంజనీర్",
      categoryLabel: "పవర్‌ట్రైన్ & బ్యాటరీ",
      location: "థానే, మహారాష్ట్ర, భారతదేశం",
      shortBio: "పవర్ ట్రైన్ సిస్టమ్స్‌పై పని చేస్తూ పనితీరు మరియు సామర్థ్యాన్ని మెరుగుపరుస్తారు."
    },
    "khushpreet-singh": {
      name: "ఖుష్‌ప్రీత్ సింగ్",
      role: "టెస్టింగ్ & వాలిడేషన్ ఇంజనీర్",
      categoryLabel: "ప్లాంట్ & తయారీ",
      location: "థానే, మహారాష్ట్ర, భారతదేశం",
      shortBio: "ఉత్పత్తుల విశ్వసనీయత మరియు భద్రతను నిర్ధారించడానికి కఠినమైన పరీక్షలు మరియు ధృవీకరణలను నిర్వహిస్తారు."
    },
    "swapnesh-jahagirdar": {
      name: "స్వప్నేష్ జహాగీర్దార్",
      role: "బిజినెస్ డెవలప్‌మెంట్ మేనేజర్",
      categoryLabel: "సేల్స్ టీమ్",
      location: "థానే / ముంబై, భారతదేశం",
      shortBio: "కంపెనీ విస్తరణను వేగవంతం చేయడానికి కొత్త వ్యాపార అవకాశాలను గుర్తించడం మరియు సంబంధాలను పెంపొందించడంపై దృష్టి సారిస్తారు."
    },
    "iv-rao": {
      name: "ఐవీ-రావు",
      role: "సీనియర్ సలహాదారు",
      categoryLabel: "వ్యూహాత్మక సలహా మండలి",
      location: "న్యూఢిల్లీ / థానే, భారతదేశం",
      shortBio: "మారుతి సుజుకి మాజీ ఎగ్జిక్యూటివ్ అడ్వైజర్ & ఇంజనీరింగ్ హెడ్. ఆటోమోటివ్ ఆర్కిటెక్చర్ మరియు సిస్టమ్స్ ఇంజనీరింగ్‌లో AutoNxt కు మార్గదర్శకత్వం."
    },
    "ashish-mehta": {
      name: "ఆశిష్ మెహతా",
      role: "ఫైనాన్షియల్ అడ్వైజర్",
      categoryLabel: "వ్యూహాత్మక సలహా మండలి",
      location: "ముంబై / థానే, భారతదేశం",
      shortBio: "మూలధన కేటాయింపు, వెంచర్ స్కేల్ మరియు స్థిరమైన వాణిజ్య విస్తరణపై సలహాలు అందించే సీనియర్ ఆర్థిక వ్యూహకర్త."
    },
    "swadeep-pillarisetti": {
      name: "స్వాదీప్ పిల్లారిశెట్టి",
      role: "వ్యూహ సలహాదారు",
      categoryLabel: "వ్యూహాత్మక సలహా మండలి",
      location: "బెంగళూరు / థానే, భారతదేశం",
      shortBio: "ఉత్పత్తి-మార్కెట్ అనుకూలత, వ్యాపార విస్తరణ మరియు ప్రభావవంతమైన భాగస్వామ్యాలను వేగవంతం చేయడంలో అంతర్జాతీయ నిపుణుడు."
    }
  }
};

/**
 * Returns a team member profile localized for the given language.
 */
export function getLocalizedTeamMember(slug: string, lang: string = "en"): TeamMemberProfile | undefined {
  const base = TEAM_MEMBERS.find((m) => m.slug === slug);
  if (!base) return undefined;
  if (lang === "en" || !TEAM_TRANSLATIONS[lang]?.[slug]) {
    return base;
  }
  const localized = TEAM_TRANSLATIONS[lang][slug];
  return {
    ...base,
    name: localized.name ?? base.name,
    role: localized.role ?? base.role,
    categoryLabel: localized.categoryLabel ?? base.categoryLabel,
    qualification: localized.qualification ?? base.qualification,
    location: localized.location ?? base.location,
    shortBio: localized.shortBio ?? base.shortBio,
    fullBio: localized.fullBio ?? base.fullBio,
    email: base.email
  };
}

export function getMemberEmail(member: { email?: string; slug: string; name: string }): string {
  if (member.email) return member.email;
  const cleanName = member.name.replace(/^Dr\.\s+/i, "").trim();
  const firstName = cleanName.split(/\s+/)[0].toLowerCase().replace(/[^a-z0-9]/g, "");
  return `${firstName}@autonxt.in`;
}

/**
 * Returns all team members localized for the given language.
 */
export function getLocalizedTeamMembers(lang: string = "en"): TeamMemberProfile[] {
  return TEAM_MEMBERS.map((m) => getLocalizedTeamMember(m.slug, lang) as TeamMemberProfile);
}

export function getTeamMemberBySlug(slug: string): TeamMemberProfile | undefined {
  return TEAM_MEMBERS.find((m) => m.slug === slug);
}
